import { Formik } from 'formik';
import _ from 'lodash';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import {
  Button,
  Buttons,
  CancelButton,
  Checkbox,
  Currency,
  DateTime,
  DeleteButton,
  DeleteConfirmation,
  Field,
  Form,
  HelpTooltip,
  Icon,
  Level,
  ModalCard,
  Table,
} from '~/components';
import { TableBoxRowActions } from '~/components/table';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import { colors, weights } from '~/styles';
import { dateFormats, emptyStringToNull, mergeValues, ordinal } from '~/utils';
import { Header } from './Header';
import { TableTitle } from './TableTitle';

const StyledCheckbox = styled.div`
  > label > div {
    background: ${colors.white};
  }
`;

const ContainerControl = styled.span`
  display: flex;
  align-items: center;
`;

const revenueTypes = {
  services: 'Services',
  expenses: 'Expenses',
  products: 'Products',
  other_items: 'Other Items',
};

function RevenueAdjustmentsLedger({ projectModel, onChange }) {
  const [formEntry, setEditForm] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const handleAdd = () => setEditForm({});
  const handleEdit = (entry) => setEditForm(entry);
  const handleCancel = () => setEditForm(null);

  const selected = useMemo(() => {
    return {
      get items() {
        return this.available.filter((item) => selectedIds.some((id) => id === item.id));
      },
      get some() {
        return this.items.length > 0;
      },
      get available() {
        return projectModel.revenueAdjustments;
      },
      get all() {
        return this.items.length === projectModel.revenueAdjustments.length;
      },
    };
  }, [selectedIds, projectModel.revenueAdjustments]);

  const handleBulkSelectionChange = () => {
    setSelectedIds(selected.some ? [] : [...selectedIds, ...projectModel.revenueAdjustments.map(({ id }) => id)]);
  };

  const handleSubmit = (value) => {
    value = emptyStringToNull(value);

    const adjustments = value.id
      ? projectModel.revenueAdjustments.map((ra) => (ra.id === value.id ? { ...ra, ...value } : ra))
      : [...projectModel.revenueAdjustments, { ...value, id: _.uniqueId('ra_') }];

    onChange(adjustments);
    handleCancel();
  };

  const handleDelete = (item) => {
    onChange(projectModel.revenueAdjustments.filter((r) => r.id !== item.id));
  };

  const handleDeleteMultiple = async () => {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>
        This will remove the selected adjustment entries from the project. Are you sure?
      </DeleteConfirmation>
    ));
    if (!confirm) return;
    onChange(projectModel.revenueAdjustments.filter((item) => !selectedIds.includes(item.id)));
  };

  const total = _.round(_.sumBy(projectModel.revenueAdjustments, 'amount'), 2);

  const confirmation = useConfirmation();
  const currency = projectModel.currency;

  const handleAddMultiple = async () => {
    await confirmation.prompt((resolve) => {
      const handleSubmit = (values) => {
        const revenueAdjustments = [
          ...projectModel.revenueAdjustments,
          ...values.map((ra) => ({ ...ra, id: _.uniqueId('ra_') })),
        ];
        onChange(revenueAdjustments);
        resolve(true);
      };

      return (
        <MultipleRevenueAdjustmentsForm
          project={projectModel}
          currency={currency}
          onSubmit={handleSubmit}
          onCancel={() => resolve(false)}
        />
      );
    });
  };

  return (
    <>
      <Header>
        <Level>
          <Level.Item>
            <TableTitle>
              Earned Revenue Adjustment Ledger
              <HelpTooltip
                message="Manual adjustments to earned revenue can be added below. These adjustments can be negative or positive and will not impact invoiced revenue."
                style={{ marginLeft: '0.5rem' }}
              />
            </TableTitle>
          </Level.Item>

          <Level.Item right narrow>
            <DeleteButton disabled={!selected.some} onClick={handleDeleteMultiple}>
              Delete
            </DeleteButton>
          </Level.Item>

          <Level.Item right narrow>
            <Button data-testid="revenue_adjustments_add_multiple_button" isOutline onClick={handleAddMultiple}>
              Add Multiple
            </Button>
          </Level.Item>
        </Level>
      </Header>

      <Form.Control>
        <Table small>
          <Table.BoxHeader>
            <Table.Column width="3rem">
              <StyledCheckbox>
                <Checkbox
                  data-testid="revenue_adjustments_bulk_checkbox"
                  disabled={!selected.available.length}
                  checked={selected.some}
                  partial={!selected.all}
                  onChange={handleBulkSelectionChange}
                />
              </StyledCheckbox>
            </Table.Column>
            <Table.Column width="8rem">Date</Table.Column>
            <Table.Column width="10rem">Revenue Type</Table.Column>
            <Table.Column>Notes</Table.Column>
            <Table.Column align="right" width="7rem">
              Amount
            </Table.Column>
            <Table.Column width="2rem" />
            <Table.BoxActionsColumn />
          </Table.BoxHeader>
          <Table.Body>
            {projectModel.revenueAdjustments.map((item) => {
              const checked = selectedIds.some((id) => id === item.id);

              const handleSelectionChange = () => {
                setSelectedIds(checked ? selectedIds.filter((id) => id !== item.id) : [...selectedIds, item.id]);
              };

              return (
                <RevenueAdjustmentRow
                  key={item.id}
                  revenueAdjustment={item}
                  currency={currency}
                  onEdit={() => handleEdit(item)}
                  onDelete={() => handleDelete(item)}
                  checked={checked}
                  onSelectionChange={() => handleSelectionChange(item)}
                />
              );
            })}

            <Table.Row>
              <Table.Cell>
                <Button data-testid="revenue_adjustments_quick_add_button" isAnchor isStrong onClick={handleAdd}>
                  <Icon icon="plus" size="xs" spaceRight />
                  Quick Add
                </Button>
              </Table.Cell>
            </Table.Row>

            <Table.Row style={{ borderBottom: 'none', fontWeight: weights.bold, textTransform: 'uppercase' }}>
              <Table.Cell>Total</Table.Cell>
              <Table.Cell />
              <Table.Cell />
              <Table.Cell />
              <Table.Cell>
                <Currency value={total} currency={currency} />
              </Table.Cell>
              <Table.Cell />
              <Table.Cell />
            </Table.Row>
          </Table.Body>
        </Table>
      </Form.Control>

      {formEntry && (
        <RevenueAdjustmentForm
          revenueAdjustment={formEntry}
          currency={currency}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
}

function RevenueAdjustmentRow({
  currency,
  revenueAdjustment: { date, revenueType, notes, amount },
  onEdit,
  onDelete,
  onSelectionChange,
  checked,
}) {
  const confirmation = useConfirmation();

  const handleDelete = async () => {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>Are you sure you want to delete this adjustment entry?</DeleteConfirmation>
    ));
    if (!confirm) return;

    onDelete();
  };

  return (
    <Table.BoxRow data-testid="revenue_adjustment">
      <Table.Cell>
        <Checkbox data-testid="checkbox_revenue_adjustment" checked={checked} onChange={onSelectionChange} />
      </Table.Cell>
      <Table.Cell>
        <DateTime value={date} />
      </Table.Cell>
      <Table.Cell>{revenueTypes[revenueType]}</Table.Cell>
      <Table.Cell>{notes}</Table.Cell>
      <Table.Cell>
        <Currency value={amount} currency={currency} />
      </Table.Cell>
      <Table.Cell />
      <TableBoxRowActions>
        <TableBoxRowActions.Edit onClick={onEdit} />

        <hr />

        <TableBoxRowActions.Delete onClick={handleDelete} />
      </TableBoxRowActions>
    </Table.BoxRow>
  );
}

function RevenueAdjustmentForm({ currency, revenueAdjustment, onSubmit, onCancel }) {
  const initialValues = mergeValues(
    {
      id: null,
      date: moment().format(dateFormats.isoDate),
      revenueType: '',
      notes: '',
      amount: '',
    },
    revenueAdjustment,
  );

  return (
    <ModalCard title={initialValues.id ? 'Edit Adjustment Entry' : 'Add Adjustment Entry'} onClose={onCancel}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          date: Yup.date().label('Date').nullable().required(),
          revenueType: Yup.string().label('Revenue Type').oneOf(Object.keys(revenueTypes)).required(),
          notes: Yup.string().label('Notes').max(255),
          amount: Yup.number().label('Amount').min(-99999999999).max(99999999999).nullable().required(),
        })}>
        {() => {
          return (
            <Form>
              <ModalCard.Body>
                <Form.Control>
                  <Field.DayPicker name="date" placeholder="Date" clearable={false} />

                  <Field.SingleSelect name="revenueType" placeholder="Revenue Type">
                    {Object.entries(revenueTypes).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </Field.SingleSelect>
                </Form.Control>

                <Form.Control>
                  <Field.Currency autoFocus name="amount" placeholder="Amount" currency={currency} />
                </Form.Control>

                <Form.Control>
                  <Field.Text name="notes" placeholder="Notes" maxLength={255} />
                </Form.Control>
              </ModalCard.Body>

              <ModalCard.Footer>
                <Buttons align="right">
                  <CancelButton onClick={onCancel}>Close</CancelButton>

                  <Button type="submit">Save &amp; Close</Button>
                </Buttons>
              </ModalCard.Footer>
            </Form>
          );
        }}
      </Formik>
    </ModalCard>
  );
}

function MultipleRevenueAdjustmentsForm({ currency, project, onSubmit, onCancel }) {
  const { workspace } = useWorkspace();
  const api = useApi();

  const initialValues = {
    start: null,
    end: null,
    dayOfMonth: null,
    revenueType: '',
    notes: '',
    amount: '',
  };

  const handleSubmit = async (values) => {
    const { start, end, dayOfMonth, revenueType, notes, amount } = emptyStringToNull(values);

    const { data: dates } = await api.www
      .workspaces(workspace.id)
      .projects(project.id)
      .generateRecurringDates({ start, end, dayOfMonth });

    const instances = dates.map((date) => {
      return {
        date: moment(date).format(dateFormats.isoDate),
        revenueType,
        notes,
        amount,
      };
    });

    onSubmit(instances);
  };

  return (
    <ModalCard title="Add Adjustment Entries" onClose={onCancel}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          start: Yup.date().label('Starting Month').nullable().required(),
          end: Yup.date()
            .label('Ending Month')
            .nullable()
            .min(Yup.ref('start'), 'Ending Month must be after Starting Month')
            .required(),
          dayOfMonth: Yup.mixed()
            .label('Day of Month')
            .oneOf([...Array(28).keys()].map((i) => i + 1).concat('last'))
            .required(),
          revenueType: Yup.string().label('Revenue Type').oneOf(Object.keys(revenueTypes)).required(),
          notes: Yup.string().label('Notes').max(255),
          amount: Yup.number().label('Amount').min(-99999999999).max(99999999999).nullable().required(),
        })}>
        {({ submitForm }) => {
          return (
            <>
              <ModalCard.Body>
                <Form.Control>
                  <Field.DayPicker
                    name="start"
                    placeholder="Starting Month"
                    locale="en-US"
                    displayFormat={dateFormats.monthYear}
                    scope="month"
                  />
                  <Field.DayPicker
                    name="end"
                    placeholder="Ending Month"
                    locale="en-US"
                    displayFormat={dateFormats.monthYear}
                    scope="month"
                  />
                </Form.Control>
                <Form.Control>
                  <ContainerControl style={{ marginTop: '0' }}>
                    Scheduled on the
                    <div style={{ margin: '0 0.5rem' }}>
                      <Field.SingleSelect
                        name="dayOfMonth"
                        style={{ width: '7rem', margin: '0 0.5rem', textAlign: 'right' }}
                        materialPlaceholder={false}>
                        {[...Array(28).keys()].map((day) => {
                          const value = day + 1;
                          return (
                            <option key={value} value={value}>
                              {ordinal(value)}
                            </option>
                          );
                        })}
                        <option value="last">Last</option>
                      </Field.SingleSelect>
                    </div>
                    <span>day of each month</span>
                  </ContainerControl>
                </Form.Control>

                <Form.Control>
                  <Field.SingleSelect name="revenueType" placeholder="Revenue Type">
                    {Object.entries(revenueTypes).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </Field.SingleSelect>

                  <Field.Currency autoFocus name="amount" placeholder="Amount" currency={currency} />
                </Form.Control>

                <Form.Control>
                  <Field.Text name="notes" placeholder="Notes" maxLength={255} />
                </Form.Control>
              </ModalCard.Body>

              <ModalCard.Footer>
                <Buttons align="right">
                  <CancelButton onClick={onCancel}>Close</CancelButton>

                  <Button onClick={submitForm}>Add</Button>
                </Buttons>
              </ModalCard.Footer>
            </>
          );
        }}
      </Formik>
    </ModalCard>
  );
}

export default RevenueAdjustmentsLedger;
